/**
 * Report message & block user (contracts moderation.ts, docs/specs/report-block.md; App Store guideline 1.2): who can
 * report or block, the report reasons and every piece of copy, which messages collapse ("Message from someone you
 * blocked · Show"), the 1:1 DM whose composer is replaced ("You blocked @name · Unblock"), and the optimistic list
 * change. Web, iOS and Android share these rules through the vectors in packages/test-vectors/logic/moderation.
 * Text is returned as message descriptors (i18n keys `moderation.*`), never translated here.
 */
import type { Channel, Member, Message, ReportReason } from "@bundle/contracts";
import { MAX_REPORT_NOTE, MAX_REPORT_PREVIEW } from "@bundle/contracts";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";

/** The reasons in the order the Report sheet lists them (radio rows), with their labels. */
export const REPORT_REASONS: readonly { reason: ReportReason; label: MessageKey }[] = [
  { reason: "spam", label: "moderation.report.reason.spam" },
  { reason: "harassment", label: "moderation.report.reason.harassment" },
  { reason: "inappropriate", label: "moderation.report.reason.inappropriate" },
  { reason: "other", label: "moderation.report.reason.other" },
];

/** The reasons, in sheet order (for ports that can't read the constant through vectors). */
export function reportReasons(): ReportReason[] {
  return REPORT_REASONS.map((r) => r.reason);
}

/** A reason's label ("Spam", "Harassment or bullying", …). */
export function reportReasonLabel(reason: ReportReason): MessageDescriptor {
  return msg(REPORT_REASONS.find((r) => r.reason === reason)?.label ?? "moderation.report.reason.other");
}

/** Is this person in my blocked list? (absent list = nobody) */
export function isBlocked(blockedUserIds: readonly string[] | undefined, userId: string): boolean {
  return blockedUserIds !== undefined && blockedUserIds.includes(userId);
}

/** "Report message" shows on messages that aren't mine and still exist (Slack: not on your own messages). */
export function canReport(message: Pick<Message, "authorId" | "deleted">, meId: string): boolean {
  return message.authorId !== meId && !message.deleted;
}

/** "Block" shows for people other than me; apps and agents can't be blocked (Slack). */
export function canBlock(user: Pick<Member, "userId" | "kind">, meId: string): boolean {
  return user.userId !== meId && user.kind === "human";
}

/** My blocked list after blocking (`blocked` = true) or unblocking someone: the optimistic update before the server's `blocks_changed`. */
export function withBlocked(blockedUserIds: readonly string[] | undefined, userId: string, blocked: boolean): string[] {
  const list = blockedUserIds ?? [];
  if (blocked) return list.includes(userId) ? [...list] : [...list, userId];
  return list.filter((id) => id !== userId);
}

/**
 * Does this message render as the collapsed row? Blocked authors' messages collapse in channels, threads and search
 * results, unless I chose Show for it in this view (`revealedIds`), and except my own and deleted ones.
 */
export function isCollapsed(message: Pick<Message, "id" | "authorId" | "deleted">, blockedUserIds: readonly string[] | undefined, meId: string, revealedIds: readonly string[] = []): boolean {
  if (message.authorId === meId || message.deleted) return false;
  return isBlocked(blockedUserIds, message.authorId) && !revealedIds.includes(message.id);
}

/**
 * In a 1:1 DM with someone I blocked, the composer is replaced by "You blocked @name · Unblock": that person's id
 * (undefined for channels, group DMs, self-DMs and people I haven't blocked).
 */
export function blockedDmPeer(channel: Pick<Channel, "kind" | "dmUserIds">, meId: string, blockedUserIds: readonly string[] | undefined): string | undefined {
  if (channel.kind !== "dm" || channel.dmUserIds === undefined) return undefined;
  const other = channel.dmUserIds.find((id) => id !== meId);
  return other !== undefined && isBlocked(blockedUserIds, other) ? other : undefined;
}

/** The note as sent: trimmed, absent when empty, cut to the server's limit. */
export function reportNote(note: string | undefined): string | undefined {
  const t = (note ?? "").trim();
  return t === "" ? undefined : t.slice(0, MAX_REPORT_NOTE);
}

/** A report's message preview: one line of plain text, whitespace collapsed, at most 280 characters (… when cut). */
export function reportPreview(text: string): string {
  const one = text.replace(/\s+/g, " ").trim();
  const chars = [...one];
  return chars.length <= MAX_REPORT_PREVIEW ? one : `${chars.slice(0, MAX_REPORT_PREVIEW - 1).join("")}…`;
}

// ---------- Copy ----------

/** Message action menu items. */
export function reportActionLabel(): MessageDescriptor {
  return msg("moderation.report.action");
}

export function blockActionLabel(name: string): MessageDescriptor {
  return msg("moderation.block.action", { name });
}

/** Profile card / sheet action. */
export function profileBlockLabel(blocked: boolean): MessageDescriptor {
  return msg(blocked ? "moderation.profile.unblock" : "moderation.profile.block");
}

/** The Report sheet. */
export function reportSheetCopy(): { title: MessageDescriptor; noteLabel: MessageDescriptor; notePlaceholder: MessageDescriptor; submit: MessageDescriptor } {
  return {
    title: msg("moderation.report.title"),
    noteLabel: msg("moderation.report.note.label"),
    notePlaceholder: msg("moderation.report.note.placeholder"),
    submit: msg("moderation.report.submit"),
  };
}

/** Toast after a report was sent. */
export function reportedToast(): MessageDescriptor {
  return msg("moderation.report.done");
}

/** The Block confirm dialog: "Block @name?" [Block] [Cancel]. */
export function blockConfirmCopy(name: string): { title: MessageDescriptor; body: MessageDescriptor; confirm: MessageDescriptor; cancel: MessageDescriptor } {
  return {
    title: msg("moderation.block.confirm.title", { name }),
    body: msg("moderation.block.confirm.body"),
    confirm: msg("moderation.block.confirm.action"),
    cancel: msg("common.cancel"),
  };
}

/** Toast after blocking or unblocking. */
export function blockedToast(name: string, blocked: boolean): MessageDescriptor {
  return msg(blocked ? "moderation.block.done" : "moderation.unblock.done", { name });
}

/** The collapsed row: "Message from someone you blocked" · "Show". */
export function collapsedRowCopy(): { text: MessageDescriptor; show: MessageDescriptor } {
  return { text: msg("moderation.collapsed.text"), show: msg("moderation.collapsed.show") };
}

/** The DM composer replacement: "You blocked @name" · "Unblock". */
export function blockedComposerCopy(name: string): { text: MessageDescriptor; unblock: MessageDescriptor } {
  return { text: msg("moderation.composer.blocked", { name }), unblock: msg("moderation.composer.unblock") };
}
